import { useEffect, useRef, useState } from "react";
import { ConsumerStat } from "@scrile/streaming-client/build/ConsumerClient";
import {
  NetworkQualityLevel,
  RawNetworkSample,
  qualityFromSample,
  reduceRtcStats,
  sampleDelta,
} from "lib/networkQualityHelpers";
import { ProduceUserData } from "../types";

const POLL_INTERVAL_MS = 2000;

// Consecutive bad samples required before flipping a remote consumer's
// video to paused. ~6 s of sustained degradation — short enough for the
// user to notice and recover from, long enough not to flap on a single
// burst.
const AUTO_PAUSE_BAD_SAMPLES_THRESHOLD = 3;
// Consecutive good samples required before resuming.
const AUTO_RESUME_GOOD_SAMPLES_THRESHOLD = 4;

export interface RemoteQuality {
  quality: NetworkQualityLevel;
  videoAutoPaused: boolean;
}

interface PerUserState {
  prev: RawNetworkSample | null;
  badStreak: number;
  goodStreak: number;
  paused: boolean;
}

interface Options {
  /** Allow the hook to call setVideoPaused() on the underlying consumer
   *  when quality is sustainedly bad. Falsey disables the fallback —
   *  diagnostics-only mode. */
  autoPauseEnabled?: boolean;
}

export default function useRemoteNetworkQualities(
  users: Record<string, ProduceUserData>,
  enabled = true,
  { autoPauseEnabled = true }: Options = {}
) {
  const [qualities, setQualities] = useState<Record<string, RemoteQuality>>({});
  const stateRef = useRef<Record<string, PerUserState>>({});
  // Keep the latest `users` reachable inside the interval without re-
  // creating the timer every render. The parent (`useConsumeController`)
  // re-creates `users` on every `isSpeaking` flip (~2 Hz), which would
  // otherwise reset our 2 s timer before it ever fires.
  const usersRef = useRef(users);
  usersRef.current = users;

  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(async () => {
      const entries = Object.entries(usersRef.current);
      if (entries.length === 0) {
        if (Object.keys(stateRef.current).length) stateRef.current = {};
        return;
      }

      const next: Record<string, RemoteQuality> = {};

      await Promise.all(entries.map(async ([userId, user]) => {
        const consumer = user.WebRTC?.webRTCData?.consumer?.client as any;
        if (!consumer?.getConsumerStats) return;

        let perConsumer: ConsumerStat[];
        try {
          perConsumer = await consumer.getConsumerStats();
        } catch {
          return;
        }
        if (!perConsumer.length) return;

        // Roll up per-user across this remote's audio + video consumers.
        // We don't care which kind contributed each metric — for the badge,
        // the worst input drives the score.
        const merged: any[] = perConsumer.flatMap((c) => c.stats || []);
        const sample = reduceRtcStats(merged);

        let st = stateRef.current[userId];
        if (!st) {
          st = { prev: null, badStreak: 0, goodStreak: 0, paused: false };
          stateRef.current[userId] = st;
        }

        const delta = sampleDelta(sample, st.prev);
        st.prev = sample;

        const level = qualityFromSample(
          {
            rtt: sample.rtt,
            packetLoss: delta.packetLoss,
            jitter: sample.jitter,
            jitterBufferDelay: sample.jitterBufferDelay,
            qualityLimitationReason: sample.qualityLimitationReason,
          },
          delta
        );

        if (level <= 2) {
          st.badStreak += 1;
          st.goodStreak = 0;
        } else if (level >= 4) {
          st.goodStreak += 1;
          st.badStreak = 0;
        } else {
          st.badStreak = 0;
          st.goodStreak = 0;
        }

        if (autoPauseEnabled) {
          if (!st.paused && st.badStreak >= AUTO_PAUSE_BAD_SAMPLES_THRESHOLD) {
            try {
              consumer.setVideoPaused(true);
              st.paused = true;
            } catch {
              /* consumer may have closed; harmless */
            }
          } else if (st.paused && st.goodStreak >= AUTO_RESUME_GOOD_SAMPLES_THRESHOLD) {
            try {
              consumer.setVideoPaused(false);
              st.paused = false;
            } catch {
              /* same as above */
            }
          }
        } else if (st.paused) {
          // autoPauseEnabled was toggled off mid-call — release any pause we
          // applied so the user doesn't get stuck audio-only.
          try {
            consumer.setVideoPaused(false);
          } catch {
            /* ignore */
          }
          st.paused = false;
        }

        next[userId] = { quality: level, videoAutoPaused: st.paused };
      }));

      // Drop state for users who left the call.
      const activeUserIds = new Set(entries.map(([id]) => id));
      Object.keys(stateRef.current).forEach((id) => {
        if (!activeUserIds.has(id)) delete stateRef.current[id];
      });

      setQualities((prev) => {
        // Avoid causing a re-render when nothing changed — keys + values
        // identical means downstream useEffect deps shouldn't fire.
        const prevKeys = Object.keys(prev);
        const nextKeys = Object.keys(next);
        if (prevKeys.length === nextKeys.length) {
          let same = true;
          for (const k of nextKeys) {
            const a = prev[k];
            const b = next[k];
            if (!a || a.quality !== b.quality || a.videoAutoPaused !== b.videoAutoPaused) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return next;
      });
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [enabled, autoPauseEnabled]);

  return qualities;
}
